import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useGql, formatRelativeOrDate, Card, CardContent, Separator } from '@ah/design';
import { Header, DetailSkeleton, PullToRefresh, SaveButton } from '@ah/design/mobile';
import { Eye, Clock } from 'lucide-react';
import {
  VIDEO_DETAIL_QUERY,
  RECORD_VIDEO_ENGAGEMENT_MUTATION,
  RECORD_VIDEO_SAVE_MUTATION,
  RECORD_VIDEO_UNSAVE_MUTATION,
  type VideoDetailData,
  type VideoSummary,
} from '../shared/index.ts';
import { formatDuration, formatViews, formatTimestamp } from './_parts.tsx';

export function VideoDetail({ inSheet }: { inSheet?: boolean } = {}) {
  const gql = useGql();
  const qc = useQueryClient();
  const { id = '' } = useParams();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['videos', 'detail', id],
    queryFn: () => gql.request<{ video: VideoDetailData | null }>(VIDEO_DETAIL_QUERY, { id }),
  });

  useEffect(() => {
    if (id) gql.request(RECORD_VIDEO_ENGAGEMENT_MUTATION, { id }).catch(() => {});
  }, [id, gql]);

  const body = isLoading ? (
    <DetailSkeleton hero="wide" />
  ) : error || !data?.video ? (
    <div className="px-4 py-10 text-center">
      <p className="text-sm text-destructive mb-3">Video not found.</p>
      <button
        type="button"
        onClick={() => refetch()}
        className="text-sm px-3 py-1.5 rounded-full border border-border bg-card"
      >
        Retry
      </button>
    </div>
  ) : (
    <VideoBody v={data.video} />
  );

  if (inSheet) return <>{body}</>;

  const trailing = data?.video ? (
    <SaveButton
      isSaved={data.video.isSaved}
      onSave={async () => {
        await gql.request(RECORD_VIDEO_SAVE_MUTATION, { id });
        qc.invalidateQueries({ queryKey: ['videos', 'saved'] });
        qc.setQueryData(['videos', 'detail', id], (prev: { video: VideoDetailData | null } | undefined) =>
          prev?.video ? { video: { ...prev.video, isSaved: true } } : prev,
        );
      }}
      onUnsave={async () => {
        await gql.request(RECORD_VIDEO_UNSAVE_MUTATION, { id });
        qc.invalidateQueries({ queryKey: ['videos', 'saved'] });
        qc.setQueryData(['videos', 'detail', id], (prev: { video: VideoDetailData | null } | undefined) =>
          prev?.video ? { video: { ...prev.video, isSaved: false } } : prev,
        );
      }}
    />
  ) : null;

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title={data?.video?.title ?? 'Video'} backTo="/videos" trailing={trailing} />
      <PullToRefresh className="flex-1" onRefresh={() => refetch()}>
        {body}
      </PullToRefresh>
    </div>
  );
}

function VideoBody({ v }: { v: VideoDetailData }) {
  const duration = formatDuration(v.duration);
  const views = formatViews(v.viewCount);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const seekTo = (sec: number) => {
    const w = iframeRef.current?.contentWindow;
    if (!w) return;
    w.postMessage(
      JSON.stringify({ event: 'command', func: 'seekTo', args: [sec, true] }),
      '*',
    );
    w.postMessage(
      JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
      '*',
    );
    iframeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <article className="pb-10 space-y-5">
      <div className="aspect-video bg-muted">
        <iframe
          ref={iframeRef}
          src={`https://www.youtube.com/embed/${v.youtubeId}?enablejsapi=1`}
          title={v.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      </div>

      <div className="px-4 space-y-5">
        <header className="space-y-2">
          <h1 className="text-xl font-bold leading-tight">{v.title}</h1>
          <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
            {duration ? <span>{duration}</span> : null}
            {views ? (
              <span className="inline-flex items-center gap-1">
                <Eye size={11} /> {views} views
              </span>
            ) : null}
            {v.datePublished ? (
              <span className="inline-flex items-center gap-1">
                <Clock size={11} /> {formatRelativeOrDate(v.datePublished)}
              </span>
            ) : null}
          </div>
          {v.summary?.topics?.length ? (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {v.summary.topics.slice(0, 6).map((t) => (
                <span
                  key={t}
                  className="text-[10px] uppercase tracking-wide rounded-full border px-2 py-0.5 text-muted-foreground"
                >
                  {t}
                </span>
              ))}
            </div>
          ) : null}
        </header>

        {v.summary ? <SummaryBody summary={v.summary} onSeek={seekTo} /> : (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground text-sm">
              {v.summaryStatus === 'failed'
                ? 'Summary generation failed for this video.'
                : v.transcriptStatus === 'ineligible'
                  ? 'No transcript available for this video.'
                  : v.transcriptStatus === 'failed'
                    ? 'Transcript fetch failed for this video.'
                    : 'This video is queued for processing. Check back in a moment.'}
            </CardContent>
          </Card>
        )}
      </div>
    </article>
  );
}

function SummaryBody({
  summary,
  onSeek,
}: {
  summary: VideoSummary;
  onSeek: (sec: number) => void;
}) {
  return (
    <div className="space-y-5">
      {summary.hook ? (
        <p className="text-base font-medium leading-relaxed text-muted-foreground">{summary.hook}</p>
      ) : null}

      {summary.whatItsAbout ? (
        <section>
          <h2 className="text-lg font-semibold mb-2">In Brief</h2>
          <p className="text-sm leading-relaxed">{summary.whatItsAbout}</p>
        </section>
      ) : null}

      {summary.longRead.length > 0 ? (
        <section>
          <h2 className="text-lg font-semibold mb-2">Full Story</h2>
          <div className="space-y-3 text-sm leading-relaxed">
            {summary.longRead.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </section>
      ) : null}

      {summary.keyTakeaways.length > 0 ? (
        <section>
          <h2 className="text-lg font-semibold mb-2">Key takeaways</h2>
          <ul className="space-y-1.5">
            {summary.keyTakeaways.map((t, i) => (
              <li key={i} className="text-sm leading-relaxed flex gap-2">
                <span className="text-muted-foreground shrink-0 mt-0.5">-</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {summary.whyItMatters ? (
        <section>
          <h2 className="text-lg font-semibold mb-2">Why it matters</h2>
          <p className="text-sm leading-relaxed">{summary.whyItMatters}</p>
        </section>
      ) : null}

      {summary.keyConcepts.length > 0 ? (
        <section>
          <h2 className="text-lg font-semibold mb-2">Key concepts</h2>
          <div className="space-y-3">
            {summary.keyConcepts.map((c, i) => (
              <div key={i}>
                <a
                  href={`https://en.wikipedia.org/wiki/${encodeURIComponent(c.wikipediaQuery)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-sm underline decoration-dotted"
                >
                  {c.term}
                </a>
                <p className="text-xs text-muted-foreground mt-0.5">{c.why}</p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {summary.keyIdeas.length > 0 ? (
        <section>
          <h2 className="text-lg font-semibold mb-2">Key ideas</h2>
          <div className="space-y-3">
            {summary.keyIdeas.map((idea, i) => (
              <div key={i}>
                <h3 className="text-sm font-semibold">{idea.title}</h3>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{idea.body}</p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {summary.keyQuotes.length > 0 ? (
        <section>
          <h2 className="text-lg font-semibold mb-2">Key quotes</h2>
          <div className="space-y-3">
            {summary.keyQuotes.map((q, i) => {
              const ts = formatTimestamp(q.timestampSec);
              const canSeek = q.timestampSec != null;
              return (
                <blockquote key={i} className="border-l-2 border-muted pl-3">
                  <p className="italic text-sm">"{q.quote}"</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    — {q.speaker}
                    {ts ? (
                      canSeek ? (
                        <>
                          {' · '}
                          <button
                            type="button"
                            onClick={() => onSeek(q.timestampSec!)}
                            className="underline tabular-nums"
                          >
                            {ts}
                          </button>
                        </>
                      ) : (
                        <span> · {ts}</span>
                      )
                    ) : null}
                  </p>
                </blockquote>
              );
            })}
          </div>
        </section>
      ) : null}

      {summary.keySpeakers.length > 0 ? (
        <section>
          <h2 className="text-lg font-semibold mb-2">Speakers</h2>
          <ul className="space-y-1.5">
            {summary.keySpeakers.map((p, i) => (
              <li key={i} className="text-sm">
                <span className="font-medium">{p.name}</span>
                <span className="text-muted-foreground"> — {p.bio}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {summary.chapters.length > 0 ? (
        <>
          <Separator />
          <section>
            <h2 className="text-lg font-semibold mb-2">Chapters</h2>
            <ol className="space-y-2">
              {summary.chapters.map((c, i) => {
                const ts = formatTimestamp(c.startSec);
                return (
                  <li key={i} className="text-sm">
                    <button
                      type="button"
                      onClick={() => onSeek(c.startSec)}
                      className="w-full text-left py-1 rounded hover:bg-muted/50 active:bg-muted"
                    >
                      <p className="font-medium">
                        {ts ? <span className="tabular-nums text-muted-foreground mr-2">{ts}</span> : null}
                        {c.title}
                      </p>
                      {c.body ? (
                        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{c.body}</p>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ol>
          </section>
        </>
      ) : null}
    </div>
  );
}
